import { handleBEFormError } from '@/helpers/beFormErrorHelper';
import { denormalizePhoneNumber, inferPhoneRegion, normalizePhoneNumber } from '@/helpers/phoneHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useAvatarEdit } from '@/services/avatar/useAvatarEdit';
import { FC, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TsForm } from '../../../../components/form/Form';
import { PhoneNumberField, PhoneNumberValue } from '../../../../components/form/PhoneNumberField';
import { TextField } from '../../../../components/form/TextInput';
import { TSTitleCard } from '../../../../components/shared/card/Card';
import { validateEmail } from '../../../../helpers/Utility';
import { useEmployeeAvatar } from '../../../../services/employee/EmployeeAvatarProvider';
import { useEmployeeAvatarApi } from '../../../../services/employee/useEmployeeAvatarApi';
import { ReadEmployee, UpdateEmployee, useUpdateSelfMutation } from '../../../../services/gql/graphql.generated';
import { useSystemSettings } from '../../../../services/settings/SystemSettingsProvider';
import strings from '../../team.translations';
import { TSTitleCardStyle } from '../AddSingleTeamMemberForm';
import { EmployeeAvatarEditCard } from './EmployeeAvatarEditCard';
import { FormActionButtons } from './FormActionButtons';
import { readEmployeeToUpdateEmployee } from '@/helpers/employeeHelper';

interface SelfTeamMemberDetailsEditFormProps {
  employee: ReadEmployee;
}

interface SelfEditFields {
  email?: string;
  phoneNumber?: PhoneNumberValue; // was: string + separate phoneRegion field
}

const toPayload = (
  data: SelfEditFields,
  employee: ReadEmployee,
  allowEssEditEmail: boolean,
  allowEssEditPhoneNumber: boolean
): UpdateEmployee => {
  const email = allowEssEditEmail ? data.email?.trim() || undefined : employee.email;
  const phoneNumber = allowEssEditPhoneNumber
    ? data.phoneNumber?.number
      ? normalizePhoneNumber(data.phoneNumber.number, data.phoneNumber.region)
      : undefined
    : employee.phoneNumber;

  return {
    ...readEmployeeToUpdateEmployee(employee),
    email,
    phoneNumber
  };
};

export const SelfTeamMemberDetailsEditForm: FC<SelfTeamMemberDetailsEditFormProps> = ({ employee }) => {
  const navigate = useNavigate();
  const allowEssEditEmail = useSystemSettings(state => state.settings.allowEssEditEmail);
  const allowEssEditPhoneNumber = useSystemSettings(state => state.settings.allowEssEditPhoneNumber);
  const [{ fetching }, updateSelf] = useUpdateSelfMutation();
  const { notifyEmployeeAvatarUpdated } = useEmployeeAvatar();
  const { uploadAvatar, removeAvatar } = useEmployeeAvatarApi(employee.identityId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { avatarUrl, uploadingAvatar, removingAvatar, handleAvatarSelected, handleAvatarRemoved } = useAvatarEdit({
    avatarUrl: employee.avatarUrl,
    uploadAvatar,
    removeAvatar,
    notifyAvatarUpdated: () => notifyEmployeeAvatarUpdated(employee.identityId),
    fileInputRef,
    messages: {
      avatarInvalidFile: strings.editTeamMember.avatarInvalidFile,
      avatarTooLarge: strings.editTeamMember.avatarTooLarge,
      avatarUploadSuccess: strings.editTeamMember.avatarUploadSuccess,
      avatarUploadError: strings.editTeamMember.avatarUploadError,
      avatarRemoveSuccess: strings.editTeamMember.avatarRemoveSuccess,
      avatarRemoveError: strings.editTeamMember.avatarRemoveError
    }
  });

  const initialRegion = inferPhoneRegion(employee.phoneNumber);

  const methods = useForm<SelfEditFields>({
    defaultValues: {
      email: employee.email ?? '',
      phoneNumber: {
        number: denormalizePhoneNumber(employee.phoneNumber, initialRegion) ?? '',
        region: initialRegion
      }
    }
  });

  const onSubmit = async (data: SelfEditFields) => {
    const payload = toPayload(data, employee, allowEssEditEmail, allowEssEditPhoneNumber);

    if (!payload.email && !payload.phoneNumber) return;

    const result = await updateSelf({ employee: payload });

    await handleMutationResult(result, {
      successMessage: 'Successfully updated.',
      errorMessage: 'Unable to update details.',
      onError: error => handleBEFormError(error, methods),
      onSuccess: () => navigate(-1)
    });
  };

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <TSTitleCard title={strings.cardTitle} className={TSTitleCardStyle}>
        <div className="space-y-5">
          <fieldset
            disabled={!allowEssEditEmail}
            className={!allowEssEditEmail ? 'cursor-not-allowed opacity-70' : undefined}
          >
            <TextField
              name="email"
              id="email"
              label={strings.common.email}
              validator={(value: string) => !allowEssEditEmail || validateEmail(value)}
              customValidatorMessage={strings.addTeamMemberForm.invalidEmail}
              inputClassName={!allowEssEditEmail ? 'pointer-events-none bg-gray-100 text-secondary-std' : undefined}
            />
          </fieldset>
          <fieldset
            disabled={!allowEssEditPhoneNumber}
            className={!allowEssEditPhoneNumber ? 'cursor-not-allowed opacity-70' : undefined}
          >
            <PhoneNumberField
              id="phoneNumber"
              name="phoneNumber"
              label={strings.common.mobileNumber}
              disabled={!allowEssEditPhoneNumber}
            />
          </fieldset>
        </div>
      </TSTitleCard>
      <EmployeeAvatarEditCard
        avatarUrl={avatarUrl}
        employeeName={strings.employeeName(employee.firstName ?? '', employee.lastName ?? '')}
        fileInputRef={fileInputRef}
        loadingRemove={removingAvatar}
        loadingUpload={uploadingAvatar}
        onAvatarSelected={handleAvatarSelected}
        onRemoveAvatar={handleAvatarRemoved}
      />
      <FormActionButtons loading={fetching} onCancel={() => navigate(-1)} />
    </TsForm>
  );
};
